/**
 * THE MAIN IMAGE'S SIZE IS A PAIR OF SELECTS NOW, NOT A NUMBER ON THE IMAGE FIELD (2026-09-17).
 *
 * Layne's ECH_MainImage v2 (Teams, 2:10 PM): one block covers every width; `imgWidth` and
 * `imgHeight` are `select` fields reading ECH_LIST_ImageWidths (500 · 560 · 600) and
 * ECH_LIST_ImageHeights (350 · 325 · 300), and `imgSrc` no longer declares `width`/`height`.
 * Three things follow for the app:
 *
 *  · THE SLOT COMES FROM THE SELECTS. Every place that used the image field's declared size —
 *    the crop frame, the Bynder pick's transform, the GIF's pixels — reads the chosen pair.
 *  · THE SELECTS LIVE UNDER THE PHOTO. Jack: "below the image, like between Animated GIF and
 *    the URL link." Two compact dropdowns in one row inside the photo card, not two sections
 *    of their own above it.
 *  · CHANGING THE SIZE RE-CUTS THE PHOTO. Layne's header is right that `{{imgHeight}}` alone
 *    moves nothing (the inline `height:auto` wins) — so the app changes the ASSET: a Bynder
 *    photo is re-asked from the transform at the new fill size, which is the one thing that
 *    makes the height choice visible in every client. Justin (2:13 PM): "defaults to 500x300
 *    no matter what you select and you can not change the size" is exactly this gap.
 */
import { bynderBase, isBynderTransform } from "./bynderCrop";

export interface ImgSizeKeys { w: string; h: string }

/**
 * The width/height select keys beside an image field — `imgWidth`/`imgHeight`, matched by shape
 * so a rename survives. HEIGHT ALONE IS A SIZE FIELD TOO (Layne's second cut, 2026-09-17 PM):
 * ECH_MainImage keeps 500 hardcoded and ECH_MainImage600 keeps 600 — only the height is a
 * dropdown, which is how Justin gets locked widths. `w` is then "" and the width comes from
 * the image field's own declaration or the block's markup (`imgSizeSlot`).
 */
export function imgSizeKeys(fields: ReadonlyArray<{ k?: string }>): ImgSizeKeys | null {
  let w = "", h = "";
  for (const f of fields) {
    const k = String(f.k || "");
    if (!w && /^(img|image|main|hero)?_?width$/i.test(k)) w = k;
    else if (!h && /^(img|image|main|hero)?_?height$/i.test(k)) h = k;
  }
  return h ? { w, h } : null;
}

/** the fixed width a block draws its image at, read off the img tag that carries the image token */
export function markupImgWidth(markup: unknown, token: string): number {
  const m = new RegExp("<img\\b[^>]*\\{\\{\\s*" + token + "\\s*\\}\\}[^>]*>", "i").exec(String(markup || ""));
  if (!m) return 0;
  const w = /\bwidth\s*=\s*["']?(\d+)/i.exec(m[0]);
  return w ? +w[1] : 0;
}

const px = (v: unknown): number => { const n = parseInt(String(v == null ? "" : v).trim(), 10); return Number.isFinite(n) && n > 0 ? n : 0; };

/**
 * The chosen size: each select's value, else its declared default; a block with no width select
 * takes `fixedW` (the image field's declared width, or the width on the block's img tag). Null
 * when either side is still unknown.
 */
export function imgSizeSlot(d: Record<string, unknown>, keys: ImgSizeKeys | null, defaults?: Record<string, unknown>, fixedW?: number): { w: number; h: number } | null {
  if (!keys) return null;
  const w = keys.w ? (px(d[keys.w]) || px(defaults && defaults[keys.w])) : (fixedW || 0);
  const h = px(d[keys.h]) || px(defaults && defaults[keys.h]);
  return w && h ? { w, h } : null;
}

/**
 * The photo re-asked at a new slot. A Bynder transform URL (sized or cropped) goes back to the
 * centre fill at the new size — the crop written for the old shape is meaningless in the new
 * one, and the fill is what a fresh pick would have stored. A GIF, a data: URL, a feed photo or
 * the placeholder are left alone: nothing here can re-cut them.
 */
export function refillForSlot(src: string, slot: { w: number; h: number }, opts?: { gif?: boolean }): string {
  if (!src || opts?.gif || /^data:/i.test(src) || !isBynderTransform(src)) return src;
  return bynderBase(src) + "?io=transform:fill,width:" + Math.round(slot.w) + ",height:" + Math.round(slot.h);
}

/** "Main Image (500x350)" → "Main Image (600×300)" once a size is chosen; a label without a size is untouched */
export function labelWithSize(label: string, slot: { w: number; h: number } | null): string {
  if (!slot) return label;
  return label.replace(/\(\s*\d+\s*[x×]\s*\d+\s*\)/, "(" + slot.w + "×" + slot.h + ")");
}
